import React, { useState } from 'react';
import { X, Users, Hash, Search, Check } from 'lucide-react';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { useToast } from '../hooks/useToast';
import API_URL from '../config/API_URL';

const FindFamiliesModal = ({ isOpen, onClose }) => {
  const [mode, setMode] = useState('code');
  const [familyCode, setFamilyCode] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const { showToast, ToastContainer } = useToast();


  if (!isOpen) return null;

  const handleJoinFamily = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await fetch(`${API_URL}/api/family/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ family_code: familyCode.trim().toUpperCase() })
      });

      if (response.ok) {
        setSuccess(true);
        setFamilyCode('');
        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 1500);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to join family. Please check the code and try again.');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery || searchQuery.length < 2) {
      setError('Please enter at least 2 characters');
      return;
    }
    
    setLoading(true);
    setError('');
    setSearchResults([]);

    try {
      const response = await fetch(`${API_URL}/api/auth/search-families?q=${encodeURIComponent(searchQuery)}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.families || []);
        if (data.families.length === 0) {
          setError('No families found matching your search');
        }
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to search families');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('An error occurred while searching');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestToJoin = async (familyId) => {
    try {
      const response = await fetch(`${API_URL}/api/family/${familyId}/request-join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        showToast('Request sent! The family will be notified.', 'success');
        setSearchResults(prev => prev.map(f => 
          f.id === familyId ? { ...f, requestSent: true } : f
        ));
      } else {
        const data = await response.json();
        showToast(data.error || 'Failed to send request', 'error');
      }
    } catch (err) {
      console.error('Error:', err);
      showToast('An error occurred', 'error');
    }
  };


  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      background: 'rgba(0,0,0,0.5)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <Card style={{ padding: '2rem', maxWidth: '600px', width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Users size={24} color="#9333ea" />
            Find Families
          </h3>
          <button 
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <X size={24} color="#6b7280" />
          </button>
        </div>

        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '1.5rem',
          padding: '0.25rem',
          background: '#f3f4f6',
          borderRadius: '0.5rem'
        }}>
          <button
            onClick={() => {
              setMode('code');
              setError('');
              setSuccess(false);
            }}
            style={{
              flex: 1,
              padding: '0.75rem',
              border: 'none',
              borderRadius: '0.375rem',
              background: mode === 'code' ? 'white' : 'transparent',
              color: mode === 'code' ? '#9333ea' : '#6b7280',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s',
              boxShadow: mode === 'code' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
            }}
          >
            <Hash style={{ width: '1rem', height: '1rem', display: 'inline', marginRight: '0.5rem' }} />
            Enter Code
          </button>
          <button
            onClick={() => {
              setMode('search');
              setError('');
              setSuccess(false);
            }}
            style={{
              flex: 1,
              padding: '0.75rem',
              border: 'none',
              borderRadius: '0.375rem',
              background: mode === 'search' ? 'white' : 'transparent',
              color: mode === 'search' ? '#9333ea' : '#6b7280',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s',
              boxShadow: mode === 'search' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
            }}
          >
            <Search style={{ width: '1rem', height: '1rem', display: 'inline', marginRight: '0.5rem' }} />
            Search Family
          </button>
        </div>

        {mode === 'code' ? (
          <>
            <div style={{ 
              background: 'linear-gradient(135deg, #fef3c7 0%, #fce7f3 100%)', 
              padding: '1.5rem', 
              borderRadius: '12px',
              marginBottom: '2rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <Hash size={20} color="#db2777" />
                <h4 style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#1f2937' }}>
                  Join a Family
                </h4>
              </div>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.25rem' }}>
                Enter the family invite code shared by parents to start reading stories to their kids
              </p>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  color: '#1f2937',
                  fontSize: '0.875rem'
                }}>
                  Family Invite Code
                </label>
                <input
                  type="text"
                  value={familyCode}
                  onChange={(e) => setFamilyCode(e.target.value.toUpperCase())}
                  maxLength={6}
                  style={{ 
                    width: '95%', 
                    padding: '0.75rem', 
                    borderRadius: '8px', 
                    border: error ? '2px solid #dc2626' : '1px solid #d1d5db',
                    fontSize: '1.25rem',
                    fontWeight: 'bold',
                    letterSpacing: '0.1em',
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    background: 'white'
                  }}
                  placeholder="ABC123"
                />
                {error && (
                  <p style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                    {error}
                  </p>
                )}
                {success && (
                  <p style={{ color: '#059669', fontSize: '0.875rem', marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Check size={16} /> Successfully joined family!
                  </p>
                )}
              </div>

              <Button 
                onClick={handleJoinFamily}
                disabled={loading || familyCode.length < 6}
                style={{ 
                  width: '100%',
                  background: loading || familyCode.length < 6 ? '#d1d5db' : '#db2777',
                  cursor: loading || familyCode.length < 6 ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Joining...' : 'Join Family'}
              </Button>
            </div>

            <div style={{ 
              borderTop: '1px solid #e5e7eb', 
              paddingTop: '1.5rem'
            }}>
              <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#6b7280', marginBottom: '1rem' }}>
                How it works:
              </h4>
              <ul style={{ 
                listStyle: 'none', 
                padding: 0, 
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem'
              }}>
                <li style={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
                  <span style={{ 
                    background: '#9333ea', 
                    color: 'white', 
                    borderRadius: '50%', 
                    width: '24px', 
                    height: '24px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    flexShrink: 0
                  }}>1</span>
                  <span style={{ color: '#4b5563', fontSize: '0.875rem' }}>
                    Ask parents for their family invite code
                  </span>
                </li>
                <li style={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
                  <span style={{ 
                    background: '#9333ea', 
                    color: 'white', 
                    borderRadius: '50%', 
                    width: '24px', 
                    height: '24px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    flexShrink: 0
                  }}>2</span>
                  <span style={{ color: '#4b5563', fontSize: '0.875rem' }}>
                    Enter the 6-character code above
                  </span>
                </li>
                <li style={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
                  <span style={{ 
                    background: '#9333ea', 
                    color: 'white', 
                    borderRadius: '50%', 
                    width: '24px', 
                    height: '24px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    flexShrink: 0
                  }}>3</span>
                  <span style={{ color: '#4b5563', fontSize: '0.875rem' }}>
                    Start recording stories for their kids!
                  </span>
                </li>
              </ul>
            </div>
          </>
        ) : (
          <div style={{ 
            background: 'linear-gradient(135deg, #fef3c7 0%, #fce7f3 100%)', 
            padding: '1.5rem', 
            borderRadius: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <Search size={20} color="#db2777" />
              <h4 style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#1f2937' }}>
                Search for a Family
              </h4>
            </div>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.25rem' }}>
              Search by family name and send a request to join
            </p>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: '#1f2937',
                fontSize: '0.875rem'
              }}>
                Family Name
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  style={{ 
                    flex: 1,
                    padding: '0.75rem', 
                    borderRadius: '8px', 
                    border: '1px solid #d1d5db',
                    fontSize: '1rem',
                    background: 'white'
                  }}
                  placeholder="Smith Family, Johnson Family..."
                />
                <Button 
                  onClick={handleSearch}
                  disabled={loading || searchQuery.length < 2}
                  style={{ 
                    background: loading || searchQuery.length < 2 ? '#d1d5db' : '#db2777',
                    cursor: loading || searchQuery.length < 2 ? 'not-allowed' : 'pointer',
                    minWidth: '100px'
                  }}
                >
                  {loading ? 'Searching...' : 'Search'}
                </Button>
              </div>
              {error && (
                <p style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  {error}
                </p>
              )}
            </div>

            {searchResults.length > 0 && (
              <div style={{ 
                background: 'white', 
                borderRadius: '8px', 
                padding: '1rem',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                <h5 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.75rem' }}>
                  Found {searchResults.length} {searchResults.length === 1 ? 'family' : 'families'}
                </h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {searchResults.map(family => (
                    <div 
                      key={family.id}
                      style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '0.75rem',
                        background: '#f9fafb',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb'
                      }}
                    >
                      <div>
                        <h6 style={{ fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem' }}>
                          {family.name}
                        </h6>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {family.kids_count || 0} {family.kids_count === 1 ? 'kid' : 'kids'}
                        </p>
                      </div>
                      <Button
                        onClick={() => handleRequestToJoin(family.id)}
                        disabled={family.requestSent}
                        variant="secondary"
                        style={{ 
                          fontSize: '0.875rem',
                          padding: '0.5rem 1rem',
                          background: family.requestSent ? '#d1d5db' : '#e5e7eb',
                          cursor: family.requestSent ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {family.requestSent ? 'Request Sent' : 'Request to Join'}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>
      <ToastContainer />
    </div>
  );
};

export default FindFamiliesModal;